import { apiClient } from './client'
import { API_ENDPOINTS } from './endpoints'
import type {
  AdminLoginRequest,
  AdminLoginResponse,
  AdminUserResponse,
  AdminUserCreateRequest,
  AdminUserCreateResponse,
  AdminRole,
  AdminRoleListResponse,
  AdminRoleCatalogResponse,
  AdminRoleWriteRequest,
  UserListRequest,
  UserListResponse,
  UserDetailsResponse,
  UpdateUserStatusRequest,
  RoleUpdateRequest,
  SuccessResponse,
  ModerationListResponse,
  ModerationListRequest,
  ModerationActionRequest,
  ModerationActionResponse,
  ModerationHistoryResponse,
  DictionaryResponse,
  RegionResponse,
  RegionCreateRequest,
  RegionUpdateRequest,
  DictionaryItemCreateRequest,
  DictionaryItemUpdateRequest,
  FAQResponse,
  FAQListResponse,
  FAQCreateRequest,
  FAQUpdateRequest,
  ActivityTypeResponse,
  ActivityTypeListResponse,
  ActivityTypeCreateRequest,
  ActivityTypeUpdateRequest,
  TransportTypeResponse,
  TransportTypeListResponse,
  TransportTypeCreateRequest,
  TransportTypeUpdateRequest,
  CourierDetailResponse,
  CourierUpdateRequest,
  LogsListResponse,
  LogStatsResponse,
  LogPreviewResponse,
  LegalEntityListRequest,
  LegalEntityListResponse,
  LegalEntityDetails,
  LegalEntityActionResponse,
  CourierTariffSummary,
  LedgerItemDTO,
  LedgerListResponse,
  ProblemOrdersResponse,
  PreviewResponse,
  TariffDetailsDTO,
  TariffListResponse,
  ClientTariffDTO,
  ClientTariffListResponse,
  ClientTariffWriteRequest,
  ClientTariffPreviewRequest,
  ClientTariffPreviewResponse,
  OrderListRequest,
  OrderListResponse,
  OrderDetails,
  OrderHistoryResponse,
  OrderFiltersResponse,
  OrderCourier,
  OrderCouriersResponse,
  CourierLoadResponse,
  AppPlatform,
  AppUpdateSettings,
  AppVersionsResponse,
  AppVersionUpdateRequest,
  AppVersionHistoryResponse,
  BalancesDTO,
  InvoiceDTO,
  InvoiceListResponse,
  InvoiceListFilters,
  InvoiceCreateRequest,
  LedgerOperationDTO,
  OperationListResponse,
  HoldListResponse,
  BalanceSettingsResponse,
  PushAudience,
  PushCampaignRequest,
  PushCampaignResponse,
  PushAudienceCountResponse,
  PushHistoryResponse,
} from './types'

// === АВТОРИЗАЦИЯ АДМИНОВ ===

export class AdminAuthService {
  /**
   * Авторизация администратора
   */
  static async login(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
    console.log('AdminAuthService.login called with:', credentials)
    console.log('Making request to:', API_ENDPOINTS.auth.login)
    
    try {
      const response = await apiClient.post<AdminLoginResponse>(
        API_ENDPOINTS.auth.login,
        credentials
      )
      console.log('Login response:', response.data)
      return response.data
    } catch (error) {
      console.error('Login request failed:', error)
      throw error
    }
  }

  /**
   * Получить информацию о текущем администраторе
   */
  static async getCurrentAdmin(): Promise<AdminUserResponse> {
    console.log('AdminAuthService.getCurrentAdmin called')
    console.log('Making request to:', API_ENDPOINTS.auth.me)
    
    const response = await apiClient.get<AdminUserResponse>(API_ENDPOINTS.auth.me)
    return response.data
  }
}

// === УПРАВЛЕНИЕ ПОЛЬЗОВАТЕЛЯМИ ===

export class AdminUserService {
  /**
   * Получить список пользователей
   */
  static async getUsers(params?: UserListRequest): Promise<UserListResponse> {
    const response = await apiClient.get<UserListResponse>(
      API_ENDPOINTS.users.list,
      { params }
    )
    return response.data
  }

  /**
   * Получить детальную информацию о пользователе
   */
  static async getUserDetails(userId: number): Promise<UserDetailsResponse> {
    const response = await apiClient.get<UserDetailsResponse>(
      API_ENDPOINTS.users.details(userId)
    )
    return response.data
  }

  /**
   * Обновить статус пользователя
   */
  static async updateUserStatus(
    userId: number,
    request: UpdateUserStatusRequest
  ): Promise<SuccessResponse> {
    const response = await apiClient.patch<SuccessResponse>(
      API_ENDPOINTS.users.updateStatus(userId),
      request
    )
    return response.data
  }

  /**
   * Обновить тип (роль) курьера
   */
  static async updateUserRole(
    userId: number,
    request: RoleUpdateRequest
  ): Promise<SuccessResponse> {
    const response = await apiClient.patch<SuccessResponse>(
      API_ENDPOINTS.users.updateRole(userId),
      request
    )
    return response.data
  }

  /** Создать сотрудника админки */
  static async createUser(request: AdminUserCreateRequest): Promise<AdminUserCreateResponse> {
    const response = await apiClient.post<AdminUserCreateResponse>(
      API_ENDPOINTS.users.create,
      request
    )
    return response.data
  }
}

// === РОЛИ АДМИНКИ (F2) ===

export class AdminRoleService {
  static async getRoles(): Promise<AdminRoleListResponse> {
    const response = await apiClient.get<AdminRoleListResponse>(API_ENDPOINTS.roles.list)
    return response.data
  }

  static async getCatalog(): Promise<AdminRoleCatalogResponse> {
    const response = await apiClient.get<AdminRoleCatalogResponse>(API_ENDPOINTS.roles.catalog)
    return response.data
  }

  static async createRole(request: AdminRoleWriteRequest): Promise<AdminRole> {
    const response = await apiClient.post<AdminRole>(API_ENDPOINTS.roles.create, request)
    return response.data
  }

  static async updateRole(id: number, request: AdminRoleWriteRequest): Promise<AdminRole> {
    const response = await apiClient.put<AdminRole>(API_ENDPOINTS.roles.update(id), request)
    return response.data
  }

  static async deleteRole(id: number): Promise<SuccessResponse> {
    const response = await apiClient.delete<SuccessResponse>(API_ENDPOINTS.roles.delete(id))
    return response.data
  }

  static async assignRole(userId: number, adminRoleId: number | null): Promise<SuccessResponse> {
    const response = await apiClient.patch<SuccessResponse>(
      API_ENDPOINTS.roles.assign(userId),
      { admin_role_id: adminRoleId }
    )
    return response.data
  }
}

// === МОДЕРАЦИЯ КУРЬЕРОВ ===

export class AdminModerationService {
  /**
   * Получить список курьеров на модерации
   */
  static async getPendingCouriers(params?: ModerationListRequest): Promise<ModerationListResponse> {
    const response = await apiClient.get<ModerationListResponse>(
      API_ENDPOINTS.couriers.pending,
      { params }
    )
    return response.data
  }

  /**
   * Одобрить курьера
   */
  static async approveCourier(
    userId: number,
    request: ModerationActionRequest
  ): Promise<ModerationActionResponse> {
    const response = await apiClient.post<ModerationActionResponse>(
      API_ENDPOINTS.couriers.approve(userId),
      request
    )
    return response.data
  }

  /**
   * Отклонить курьера
   */
  static async rejectCourier(
    userId: number,
    request: ModerationActionRequest
  ): Promise<ModerationActionResponse> {
    const response = await apiClient.post<ModerationActionResponse>(
      API_ENDPOINTS.couriers.reject(userId),
      request
    )
    return response.data
  }

  /**
   * Вернуть заявку курьера на модерацию (reopen)
   */
  static async reopenCourier(
    userId: number,
    request: { expected_status_moderation?: number }
  ): Promise<ModerationActionResponse> {
    const response = await apiClient.post<ModerationActionResponse>(
      API_ENDPOINTS.couriers.reopen(userId),
      request
    )
    return response.data
  }

  /**
   * Получить историю модерации курьера
   */
  static async getModerationHistory(userId: number): Promise<ModerationHistoryResponse> {
    const response = await apiClient.get<ModerationHistoryResponse>(
      API_ENDPOINTS.couriers.moderationHistory(userId)
    )
    return response.data
  }
}

// === СПРАВОЧНИКИ ===

export class AdminDictionaryService {
  // === РЕГИОНЫ ===
  
  /**
   * Получить регионы
   */
  static async getRegions(includeInactive: boolean = false): Promise<RegionResponse[]> {
    const response = await apiClient.get<RegionResponse[]>(
      API_ENDPOINTS.dictionaries.regions.list,
      { params: { include_inactive: includeInactive } }
    )
    return response.data
  }

  /**
   * Создать регион
   */
  static async createRegion(request: RegionCreateRequest): Promise<RegionResponse> {
    const response = await apiClient.post<RegionResponse>(
      API_ENDPOINTS.dictionaries.regions.create,
      request
    )
    return response.data
  }

  /**
   * Обновить регион
   */
  static async updateRegion(id: number, request: RegionUpdateRequest): Promise<RegionResponse> {
    const response = await apiClient.put<RegionResponse>(
      API_ENDPOINTS.dictionaries.regions.update(id),
      request
    )
    return response.data
  }

  /**
   * Удалить регион
   */
  static async deleteRegion(id: number): Promise<SuccessResponse> {
    const response = await apiClient.delete<SuccessResponse>(
      API_ENDPOINTS.dictionaries.regions.delete(id)
    )
    return response.data
  }

  // === ВИДЫ ДЕЯТЕЛЬНОСТИ ===

  /**
   * Получить виды деятельности
   */
  static async getActivityTypes(includeInactive: boolean = false): Promise<DictionaryResponse[]> {
    const response = await apiClient.get<DictionaryResponse[]>(
      API_ENDPOINTS.dictionaries.activityTypes.list,
      { params: { include_inactive: includeInactive } }
    )
    return response.data
  }

  /**
   * Создать вид деятельности
   */
  static async createActivityType(request: DictionaryItemCreateRequest): Promise<DictionaryResponse> {
    const response = await apiClient.post<DictionaryResponse>(
      API_ENDPOINTS.dictionaries.activityTypes.create,
      request
    )
    return response.data
  }

  /**
   * Обновить вид деятельности
   */
  static async updateActivityType(id: number, request: DictionaryItemUpdateRequest): Promise<DictionaryResponse> {
    const response = await apiClient.put<DictionaryResponse>(
      API_ENDPOINTS.dictionaries.activityTypes.update(id),
      request
    )
    return response.data
  }

  /**
   * Удалить вид деятельности
   */
  static async deleteActivityType(id: number): Promise<SuccessResponse> {
    const response = await apiClient.delete<SuccessResponse>(
      API_ENDPOINTS.dictionaries.activityTypes.delete(id)
    )
    return response.data
  }

  // === ВИДЫ ТРАНСПОРТА ===

  /**
   * Получить виды транспорта
   */
  static async getTransportTypes(includeInactive: boolean = false): Promise<DictionaryResponse[]> {
    const response = await apiClient.get<DictionaryResponse[]>(
      API_ENDPOINTS.dictionaries.transportTypes.list,
      { params: { include_inactive: includeInactive } }
    )
    return response.data
  }

  /**
   * Создать вид транспорта
   */
  static async createTransportType(request: DictionaryItemCreateRequest): Promise<DictionaryResponse> {
    const response = await apiClient.post<DictionaryResponse>(
      API_ENDPOINTS.dictionaries.transportTypes.create,
      request
    )
    return response.data
  }

  /**
   * Обновить вид транспорта
   */
  static async updateTransportType(id: number, request: DictionaryItemUpdateRequest): Promise<DictionaryResponse> {
    const response = await apiClient.put<DictionaryResponse>(
      API_ENDPOINTS.dictionaries.transportTypes.update(id),
      request
    )
    return response.data
  }

  /**
   * Удалить вид транспорта
   */
  static async deleteTransportType(id: number): Promise<SuccessResponse> {
    const response = await apiClient.delete<SuccessResponse>(
      API_ENDPOINTS.dictionaries.transportTypes.delete(id)
    )
    return response.data
  }
}

// === FAQ (ЧАСТЫЕ ВОПРОСЫ) ===

export class AdminFAQService {
  /**
   * Получить список FAQ
   */
  static async getFAQList(params?: {
    page?: number
    limit?: number
    search?: string
    include_inactive?: boolean
  }): Promise<FAQListResponse> {
    const response = await apiClient.get<FAQListResponse>(
      API_ENDPOINTS.faq.list,
      { params }
    )
    return response.data
  }

  /**
   * Создать FAQ
   */
  static async createFAQ(request: FAQCreateRequest): Promise<FAQResponse> {
    const response = await apiClient.post<FAQResponse>(
      API_ENDPOINTS.faq.create,
      request
    )
    return response.data
  }

  /**
   * Обновить FAQ
   */
  static async updateFAQ(id: number, request: FAQUpdateRequest): Promise<FAQResponse> {
    const response = await apiClient.put<FAQResponse>(
      API_ENDPOINTS.faq.update(id),
      request
    )
    return response.data
  }

  /**
   * Удалить FAQ
   */
  static async deleteFAQ(id: number): Promise<SuccessResponse> {
    const response = await apiClient.delete<SuccessResponse>(
      API_ENDPOINTS.faq.delete(id)
    )
    return response.data
  }
}

// === УПРАВЛЕНИЕ ВИДАМИ ДЕЯТЕЛЬНОСТИ ===

export class ActivityTypeService {
  /**
   * Получить список видов деятельности
   */
  static async getActivityTypesList(params?: {
    page?: number
    limit?: number
    search?: string
    include_inactive?: boolean
  }): Promise<ActivityTypeListResponse> {
    const response = await apiClient.get<ActivityTypeListResponse>(
      API_ENDPOINTS.activityTypes.list,
      { params }
    )
    return response.data
  }

  /**
   * Создать вид деятельности
   */
  static async createActivityType(request: ActivityTypeCreateRequest): Promise<ActivityTypeResponse> {
    const response = await apiClient.post<ActivityTypeResponse>(
      API_ENDPOINTS.activityTypes.create,
      request
    )
    return response.data
  }

  /**
   * Обновить вид деятельности
   */
  static async updateActivityType(id: number, request: ActivityTypeUpdateRequest): Promise<ActivityTypeResponse> {
    const response = await apiClient.put<ActivityTypeResponse>(
      API_ENDPOINTS.activityTypes.update(id),
      request
    )
    return response.data
  }

  /**
   * Удалить вид деятельности
   */
  static async deleteActivityType(id: number): Promise<SuccessResponse> {
    const response = await apiClient.delete<SuccessResponse>(
      API_ENDPOINTS.activityTypes.delete(id)
    )
    return response.data
  }
}

// === УПРАВЛЕНИЕ ВИДАМИ ТРАНСПОРТА ===

export class TransportTypeService {
  /**
   * Получить список видов транспорта
   */
  static async getTransportTypesList(params?: {
    page?: number
    limit?: number
    search?: string
    include_inactive?: boolean
  }): Promise<TransportTypeListResponse> {
    const response = await apiClient.get<TransportTypeListResponse>(
      API_ENDPOINTS.transportTypes.list,
      { params }
    )
    return response.data
  }

  /**
   * Создать вид транспорта
   */
  static async createTransportType(request: TransportTypeCreateRequest): Promise<TransportTypeResponse> {
    const response = await apiClient.post<TransportTypeResponse>(
      API_ENDPOINTS.transportTypes.create,
      request
    )
    return response.data
  }

  /**
   * Обновить вид транспорта
   */
  static async updateTransportType(id: number, request: TransportTypeUpdateRequest): Promise<TransportTypeResponse> {
    const response = await apiClient.put<TransportTypeResponse>(
      API_ENDPOINTS.transportTypes.update(id),
      request
    )
    return response.data
  }

  /**
   * Удалить вид транспорта
   */
  static async deleteTransportType(id: number): Promise<SuccessResponse> {
    const response = await apiClient.delete<SuccessResponse>(
      API_ENDPOINTS.transportTypes.delete(id)
    )
    return response.data
  }
}

// === ЛОГИ API ЗАПРОСОВ ===

export class LogsService {
  /**
   * Получить список всех файлов логов
   * Доступно при наличии права audit.view
   */
  static async getLogsList(): Promise<LogsListResponse> {
    const response = await apiClient.get<LogsListResponse>(
      API_ENDPOINTS.logs.list
    )
    return response.data
  }

  /**
   * Получить статистику текущего файла логов
   * Доступно при наличии права audit.view
   */
  static async getCurrentLogStats(): Promise<LogStatsResponse> {
    const response = await apiClient.get<LogStatsResponse>(
      API_ENDPOINTS.logs.current
    )
    return response.data
  }

  /**
   * Предпросмотр файла лога
   * Доступно при наличии права audit.view
   */
  static async previewLog(filename: string, maxLines: number = 100): Promise<LogPreviewResponse> {
    const response = await apiClient.get<LogPreviewResponse>(
      API_ENDPOINTS.logs.preview(filename),
      { params: { max_lines: maxLines } }
    )
    return response.data
  }

  /**
   * Скачать файл лога
   * Доступно при наличии права audit.view
   */
  static getDownloadUrl(filename: string): string {
    const token = localStorage.getItem('admin_token')
    const baseURL = apiClient.defaults.baseURL || ''
    return `${baseURL}${API_ENDPOINTS.logs.download(filename)}?token=${token}`
  }

  /**
   * Скачать файл лога (программно)
   * Использует fetch API с правильной обработкой blob
   */
  static async downloadLog(filename: string): Promise<void> {
    try {
      const token = localStorage.getItem('admin_token')
      const baseURL = apiClient.defaults.baseURL || ''
      const url = `${baseURL}${API_ENDPOINTS.logs.download(filename)}`
      
      // Используем fetch вместо axios для лучшей поддержки blob
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      // Получаем blob из ответа
      const blob = await response.blob()
      
      // Создаём ссылку для скачивания
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      
      // Очистка
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Error downloading log file:', error)
      throw error
    }
  }
}

// === ЮРИДИЧЕСКИЕ ЛИЦА ===

export class AdminLegalEntityService {
  static async getLegalEntities(
    params?: LegalEntityListRequest
  ): Promise<LegalEntityListResponse> {
    const response = await apiClient.get<LegalEntityListResponse>(
      API_ENDPOINTS.legalEntities.list,
      { params }
    )
    return response.data
  }

  static async getLegalEntityDetails(id: number): Promise<LegalEntityDetails> {
    const response = await apiClient.get<LegalEntityDetails>(
      API_ENDPOINTS.legalEntities.details(id)
    )
    return response.data
  }

  static async approveLegalEntity(id: number): Promise<LegalEntityActionResponse> {
    const response = await apiClient.post<LegalEntityActionResponse>(
      API_ENDPOINTS.legalEntities.approve(id)
    )
    return response.data
  }

  static async rejectLegalEntity(
    id: number,
    reason: string
  ): Promise<LegalEntityActionResponse> {
    const response = await apiClient.post<LegalEntityActionResponse>(
      API_ENDPOINTS.legalEntities.reject(id),
      { reason }
    )
    return response.data
  }

  static async disableLegalEntity(
    id: number,
    reason: string
  ): Promise<LegalEntityActionResponse> {
    const response = await apiClient.post<LegalEntityActionResponse>(
      API_ENDPOINTS.legalEntities.disable(id),
      { reason }
    )
    return response.data
  }

  static async enableLegalEntity(id: number): Promise<LegalEntityActionResponse> {
    const response = await apiClient.post<LegalEntityActionResponse>(
      API_ENDPOINTS.legalEntities.enable(id)
    )
    return response.data
  }
}

// === ТАРИФЫ КУРЬЕРОВ ===

export class AdminTariffService {
  static async list(params?: Record<string, unknown>): Promise<TariffListResponse> {
    return (await apiClient.get(API_ENDPOINTS.tariffs.list, { params })).data
  }

  static async details(id: number): Promise<TariffDetailsDTO> {
    return (await apiClient.get(API_ENDPOINTS.tariffs.details(id))).data
  }

  static async create(body: unknown): Promise<TariffDetailsDTO> {
    return (await apiClient.post(API_ENDPOINTS.tariffs.create, body)).data
  }

  static async duplicate(id: number): Promise<TariffDetailsDTO> {
    return (await apiClient.post(API_ENDPOINTS.tariffs.duplicate(id))).data
  }

  static async remove(id: number): Promise<{ success: boolean }> {
    return (await apiClient.delete(API_ENDPOINTS.tariffs.remove(id))).data
  }

  static async setCourier(id: number, courierId: number): Promise<TariffDetailsDTO> {
    return (await apiClient.put(API_ENDPOINTS.tariffs.setCourier(id), { courier_id: courierId })).data
  }

  static async update(id: number, body: unknown): Promise<TariffDetailsDTO> {
    return (await apiClient.put(API_ENDPOINTS.tariffs.update(id), body)).data
  }

  static async createVersion(id: number): Promise<TariffDetailsDTO> {
    return (await apiClient.post(API_ENDPOINTS.tariffs.createVersion(id))).data
  }

  static async updateVersion(
    id: number,
    versionId: number,
    body: unknown
  ): Promise<TariffDetailsDTO> {
    return (await apiClient.put(API_ENDPOINTS.tariffs.updateVersion(id, versionId), body)).data
  }

  static async publishVersion(id: number, versionId: number): Promise<TariffDetailsDTO> {
    return (await apiClient.post(API_ENDPOINTS.tariffs.publishVersion(id, versionId))).data
  }

  static async setActive(id: number, active: boolean): Promise<TariffDetailsDTO> {
    const url = active ? API_ENDPOINTS.tariffs.activate(id) : API_ENDPOINTS.tariffs.deactivate(id)
    return (await apiClient.post(url)).data
  }

  static async preview(body: unknown): Promise<PreviewResponse> {
    return (await apiClient.post(API_ENDPOINTS.tariffs.preview, body)).data
  }

  static async courierSummary(courierId: number): Promise<CourierTariffSummary> {
    return (await apiClient.get(API_ENDPOINTS.tariffs.courierSummary(courierId))).data
  }
}

export class AdminCourierLedgerService {
  static async list(params?: Record<string, unknown>): Promise<LedgerListResponse> {
    return (await apiClient.get(API_ENDPOINTS.courierLedger.list, { params })).data
  }

  static async details(id: number): Promise<LedgerItemDTO & { breakdown: string | null }> {
    return (await apiClient.get(API_ENDPOINTS.courierLedger.details(id))).data
  }

  static async recalculate(id: number, body: { new_amount: number; reason: string }) {
    return (await apiClient.post(API_ENDPOINTS.courierLedger.recalculate(id), body)).data
  }

  // Заказы, по которым начисление не сформировано (нет настроенного тарифа)
  static async problemOrders(params?: Record<string, unknown>): Promise<ProblemOrdersResponse> {
    return (await apiClient.get(API_ENDPOINTS.courierLedger.problemOrders, { params })).data
  }

  // Выгрузка журнала начислений в CSV с учётом текущих фильтров
  static async exportCsv(params?: Record<string, unknown>): Promise<Blob> {
    const response = await apiClient.get(API_ENDPOINTS.courierLedger.exportCsv, {
      params,
      responseType: 'blob',
    })
    return response.data
  }
}

// === ТАРИФЫ ЮРЛИЦ (КОНСТРУКТОР) ===

export class AdminClientTariffService {
  static async list(params?: Record<string, unknown>): Promise<ClientTariffListResponse> {
    return (await apiClient.get(API_ENDPOINTS.clientTariffs.list, { params })).data
  }

  static async details(id: number): Promise<ClientTariffDTO> {
    return (await apiClient.get(API_ENDPOINTS.clientTariffs.details(id))).data
  }

  static async create(body: ClientTariffWriteRequest): Promise<ClientTariffDTO> {
    return (await apiClient.post(API_ENDPOINTS.clientTariffs.create, body)).data
  }

  static async update(id: number, body: ClientTariffWriteRequest): Promise<ClientTariffDTO> {
    return (await apiClient.put(API_ENDPOINTS.clientTariffs.update(id), body)).data
  }

  static async setActive(id: number, active: boolean): Promise<ClientTariffDTO> {
    const url = active ? API_ENDPOINTS.clientTariffs.activate(id) : API_ENDPOINTS.clientTariffs.pause(id)
    return (await apiClient.post(url)).data
  }

  static async remove(id: number): Promise<{ success: boolean }> {
    return (await apiClient.delete(API_ENDPOINTS.clientTariffs.remove(id))).data
  }

  static async preview(body: ClientTariffPreviewRequest): Promise<ClientTariffPreviewResponse> {
    return (await apiClient.post(API_ENDPOINTS.clientTariffs.preview, body)).data
  }
}

// === ЗАКАЗЫ (READ-ONLY) ===

export class AdminOrderService {
  /**
   * Список заказов с фильтрами и пагинацией.
   *
   * Backend принимает snake_case query-параметры, а `OrderListRequest` —
   * camelCase. Преобразуем явно: иначе axios отправит camelCase-ключи,
   * FastAPI их проигнорирует и фильтры молча не применятся.
   */
  static async getOrders(params?: OrderListRequest): Promise<OrderListResponse> {
    const query: Record<string, string | number | undefined> = {
      page: params?.page,
      limit: params?.limit,
      tab: params?.tab,
      status: params?.status,
      search: params?.search,
      delivery_type: params?.deliveryType,
      legal_entity_id: params?.legalEntityId,
      courier_id: params?.courierId,
      date_type: params?.dateType,
      date_from: params?.dateFrom,
      date_to: params?.dateTo,
    }

    const response = await apiClient.get<OrderListResponse>(
      API_ENDPOINTS.orders.list,
      { params: query }
    )
    return response.data
  }

  /** Карточка заказа (read-only) */
  static async getOrderDetails(id: number): Promise<OrderDetails> {
    const response = await apiClient.get<OrderDetails>(
      API_ENDPOINTS.orders.details(id)
    )
    return response.data
  }

  /** История изменений заказа по возрастанию времени */
  static async getOrderHistory(id: number): Promise<OrderHistoryResponse> {
    const response = await apiClient.get<OrderHistoryResponse>(
      API_ENDPOINTS.orders.history(id)
    )
    return response.data
  }

  /** Справочники фильтров списка заказов */
  static async getOrderFilters(): Promise<OrderFiltersResponse> {
    const response = await apiClient.get<OrderFiltersResponse>(
      API_ENDPOINTS.orders.filters
    )
    return response.data
  }

  /**
   * Активные курьеры; с датой — нагрузка на этот день.
   * Backend отдаёт `{ items }`, наружу — плоский список.
   */
  static async getOrderCouriers(date?: number): Promise<OrderCourier[]> {
    const response = await apiClient.get<OrderCouriersResponse>(
      API_ENDPOINTS.orders.couriers,
      date !== undefined ? { params: { date } } : undefined
    )
    return response.data.items
  }

  /** Нагрузка курьера на дату: сводка и список заказов */
  static async getCourierLoad(
    id: number,
    date: number
  ): Promise<CourierLoadResponse> {
    const response = await apiClient.get<CourierLoadResponse>(
      API_ENDPOINTS.orders.courierLoad(id, date)
    )
    return response.data
  }
}

// === ВЕРСИИ ПРИЛОЖЕНИЯ (АДМИНКА) ===

export class AdminAppVersionService {
  /** Текущие настройки обновления всех платформ */
  static async list(): Promise<AppVersionsResponse> {
    const response = await apiClient.get<AppVersionsResponse>(
      API_ENDPOINTS.appVersions.list
    )
    return response.data
  }

  /** Полное сохранение настроек платформы */
  static async update(
    platform: AppPlatform,
    request: AppVersionUpdateRequest
  ): Promise<AppUpdateSettings> {
    const response = await apiClient.put<AppUpdateSettings>(
      API_ENDPOINTS.appVersions.update(platform),
      request
    )
    return response.data
  }

  /** Включить/выключить кампанию обновления платформы */
  static async toggle(
    platform: AppPlatform,
    isActive: boolean
  ): Promise<AppUpdateSettings> {
    const response = await apiClient.post<AppUpdateSettings>(
      API_ENDPOINTS.appVersions.toggle(platform),
      { is_active: isActive }
    )
    return response.data
  }

  /** История изменений настроек; платформа — необязательный фильтр */
  static async history(
    platform?: AppPlatform
  ): Promise<AppVersionHistoryResponse> {
    const response = await apiClient.get<AppVersionHistoryResponse>(
      API_ENDPOINTS.appVersions.history,
      platform ? { params: { platform } } : undefined
    )
    return response.data
  }
}
// === ДЕМО-БАЛАНС ЮРЛИЦА (СЧЕТА И ОПЕРАЦИИ) ===

/** Фильтры списка счетов приходят camelCase, бэкенд ждёт snake_case. */
const toInvoiceQuery = (params?: InvoiceListFilters) => ({
  page: params?.page,
  limit: params?.limit,
  status: params?.status,
  legal_entity_id: params?.legalEntityId,
  search: params?.search,
  date_from: params?.dateFrom,
  date_to: params?.dateTo,
})

/** Имя файла из `Content-Disposition` (RFC 5987 + ASCII-fallback). */
const parseContentDispositionFilename = (
  disposition?: string
): string | null => {
  if (!disposition) return null
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim())
    } catch {
      return encoded[1].trim()
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1]?.trim() || null
}

export class AdminBalanceService {
  /** Снимок баланса компании */
  static async balance(entityId: number): Promise<BalancesDTO> {
    const response = await apiClient.get<BalancesDTO>(
      API_ENDPOINTS.balance.entityBalance(entityId)
    )
    return response.data
  }

  /** Счета всех юрлиц с фильтрами по статусу, юрлицу, периоду и номеру */
  static async invoices(params?: InvoiceListFilters): Promise<InvoiceListResponse> {
    const response = await apiClient.get<InvoiceListResponse>(
      API_ENDPOINTS.balance.invoices,
      { params: toInvoiceQuery(params) }
    )
    return response.data
  }

  /** Журнал операций компании страницей */
  static async operations(
    entityId: number,
    params?: { page?: number; limit?: number }
  ): Promise<OperationListResponse> {
    const response = await apiClient.get<OperationListResponse>(
      API_ENDPOINTS.balance.entityOperations(entityId),
      { params }
    )
    return response.data
  }

  /** Активные резервы компании */
  static async holds(entityId: number): Promise<HoldListResponse> {
    const response = await apiClient.get<HoldListResponse>(
      API_ENDPOINTS.balance.entityHolds(entityId)
    )
    return response.data
  }

  /** Выставить счёт из админки (пополнение только после подтверждения) */
  static async createInvoice(
    entityId: number,
    body: InvoiceCreateRequest
  ): Promise<InvoiceDTO> {
    const response = await apiClient.post<InvoiceDTO>(
      API_ENDPOINTS.balance.createInvoice(entityId),
      body
    )
    return response.data
  }

  /** Подтвердить демонстрационную оплату счёта */
  static async confirmInvoice(invoiceId: number): Promise<InvoiceDTO> {
    const response = await apiClient.post<InvoiceDTO>(
      API_ENDPOINTS.balance.confirm(invoiceId)
    )
    return response.data
  }

  /** Аннулировать неоплаченный счёт с обязательной причиной */
  static async cancelInvoice(invoiceId: number, reason: string): Promise<InvoiceDTO> {
    const response = await apiClient.post<InvoiceDTO>(
      API_ENDPOINTS.balance.cancel(invoiceId),
      { reason }
    )
    return response.data
  }

  /** Освободить зависший резерв вручную */
  static async releaseHold(
    entityId: number,
    entryId: number
  ): Promise<LedgerOperationDTO> {
    const response = await apiClient.post<LedgerOperationDTO>(
      API_ENDPOINTS.balance.releaseHold(entityId, entryId)
    )
    return response.data
  }

  /** Включить или выключить демо-режим компании */
  static async updateSettings(
    entityId: number,
    enabled: boolean
  ): Promise<BalanceSettingsResponse> {
    const response = await apiClient.patch<BalanceSettingsResponse>(
      API_ENDPOINTS.balance.settings(entityId),
      { enabled }
    )
    return response.data
  }

  /** PDF счёта: содержимое и имя файла из Content-Disposition */
  static async invoicePdf(
    invoiceId: number
  ): Promise<{ blob: Blob; filename: string | null }> {
    const response = await apiClient.get<Blob>(
      API_ENDPOINTS.balance.pdf(invoiceId),
      { responseType: 'blob' }
    )
    const disposition = response.headers?.['content-disposition']
    return {
      blob: response.data,
      filename: parseContentDispositionFilename(
        typeof disposition === 'string' ? disposition : undefined
      ),
    }
  }
}

// === ПУШ-РАССЫЛКИ ===

export class AdminPushService {
  /** Количество получателей в выбранной аудитории */
  static async audienceCount(
    audience: PushAudience
  ): Promise<PushAudienceCountResponse> {
    const response = await apiClient.get<PushAudienceCountResponse>(
      API_ENDPOINTS.push.audience,
      { params: { audience } }
    )
    return response.data
  }

  /** Отправить рассылку выбранной аудитории */
  static async send(
    request: PushCampaignRequest
  ): Promise<PushCampaignResponse> {
    const response = await apiClient.post<PushCampaignResponse>(
      API_ENDPOINTS.push.campaigns,
      request
    )
    return response.data
  }

  /** История рассылок и системных пушей */
  static async history(
    page: number,
    limit = 20
  ): Promise<PushHistoryResponse> {
    const response = await apiClient.get<PushHistoryResponse>(
      API_ENDPOINTS.push.history,
      { params: { page, limit } }
    )
    return response.data
  }

  /** Повторная отправка сохранённой кампании */
  static async resend(id: number): Promise<PushCampaignResponse> {
    const response = await apiClient.post<PushCampaignResponse>(
      API_ENDPOINTS.push.resend(id)
    )
    return response.data
  }
}
